/**
 * Deferred Parameter Apply Engine
 * ───────────────────────────────
 * The crux of the extension: parameter edits are buffered as a draft and only
 * pushed to Tableau when the user clicks Go (or executes a saved combination).
 *
 * `applyParameters` writes every changed value to Tableau in one batch. Tableau
 * then refreshes worksheets, calculations and KPIs — a single coordinated
 * refresh instead of one-per-keystroke, which is the whole point of deferral.
 */

import type {
  ParameterAssignment,
  ParameterDescriptor,
  ParameterValue,
} from "@/types";

/** Coerce our JSON-safe value into the type Tableau's changeValueAsync expects. */
function toTableauValue(
  descriptor: ParameterDescriptor,
  value: ParameterValue,
): string | number | boolean | Date {
  switch (descriptor.dataType) {
    case "bool":
      return Boolean(value);
    case "int":
    case "float":
      return Number(value ?? 0);
    case "date":
    case "date-time":
      return value ? new Date(value as string) : new Date();
    default:
      return value === null ? "" : String(value);
  }
}

export interface ApplyResult {
  applied: ParameterAssignment[];
  /** Parameters that failed to apply, with the error. */
  failures: { parameterId: string; error: string }[];
}

/**
 * Push the draft values to Tableau. Only parameters present in `values` are
 * written. Each write is awaited; failures are collected, not thrown, so one bad
 * parameter doesn't abort the whole batch.
 */
export async function applyParameters(
  descriptors: ParameterDescriptor[],
  values: Record<string, ParameterValue>,
): Promise<ApplyResult> {
  const dashboard = globalThis.tableau?.extensions?.dashboardContent?.dashboard;
  if (!dashboard) {
    throw new Error("Tableau dashboard unavailable — cannot apply parameters.");
  }

  const applied: ParameterAssignment[] = [];
  const failures: ApplyResult["failures"] = [];

  // Fetch once and index by id so a batch of writes resolves parameters cheaply
  // and is resilient to name collisions.
  const liveParams = await dashboard.getParametersAsync();
  const byId = new Map(liveParams.map((p) => [p.id, p]));
  const byName = new Map(liveParams.map((p) => [p.name, p]));

  for (const descriptor of descriptors) {
    if (!(descriptor.id in values)) continue;
    const value = values[descriptor.id];
    try {
      const param = byId.get(descriptor.id) ?? byName.get(descriptor.name);
      if (!param) {
        failures.push({ parameterId: descriptor.id, error: "Parameter not found" });
        continue;
      }
      const result = await param.changeValueAsync(toTableauValue(descriptor, value));
      applied.push({
        parameterId: descriptor.id,
        parameterName: descriptor.name,
        dataType: descriptor.dataType,
        value,
        displayValue: result?.formattedValue ?? formatDisplay(descriptor, value),
      });
    } catch (err) {
      failures.push({
        parameterId: descriptor.id,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  return { applied, failures };
}

/** Build the display value used in signatures when Tableau doesn't give one. */
export function formatDisplay(
  descriptor: ParameterDescriptor,
  value: ParameterValue,
): string {
  if (value === null || value === "") return "—";
  if (descriptor.controlType === "boolean") return value ? "True" : "False";
  if (descriptor.allowableValues) {
    const match = descriptor.allowableValues.find((a) => String(a.value) === String(value));
    if (match) return match.label;
  }
  if (descriptor.dataType === "date" || descriptor.dataType === "date-time") {
    const d = new Date(value as string);
    if (!Number.isNaN(d.getTime())) {
      return descriptor.dataType === "date"
        ? d.toLocaleDateString()
        : d.toLocaleString();
    }
  }
  return String(value);
}

/**
 * Build the full assignment list from descriptors + draft values, independent of
 * whether the apply succeeded. Used so tracking captures intent even if a single
 * parameter write transiently fails.
 */
export function buildAssignments(
  descriptors: ParameterDescriptor[],
  values: Record<string, ParameterValue>,
): ParameterAssignment[] {
  return descriptors
    .filter((d) => d.id in values)
    .map((d) => ({
      parameterId: d.id,
      parameterName: d.name,
      dataType: d.dataType,
      value: values[d.id],
      displayValue: formatDisplay(d, values[d.id]),
    }));
}
